import { useContext, useEffect, useState, useMemo } from "react";
import { FaPlus } from "react-icons/fa";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CourseCalender from "./charts/CourseCalender";
import TotalLearnersChart from "./charts/TotalLearnersChart";
import RecentCoursesTable from "./RecentCoursesTable";
import AddCourseModal from "./AddCourseModal";
import TotalCountsCard from "./charts/TotalCountsCards";

const STORAGE_KEY = "courses_dashboard_top_cards_order";

const SortableItem = ({ id, children, activeId }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const isDragging = activeId === id;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? "grabbing" : "default",
  };

  return (
    <Col
      xs={12}
      md={6}
      xl={4}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div className={isDragging ? "dragging" : ""}>{children}</div>
    </Col>
  );
};

const saveToLocalStorage = (ids) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore errors
  }
};

const loadFromLocalStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const CoursesDashboard = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [reloadTable, setReloadTable] = useState(false);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

useEffect(() => {
  const courseAccess = authContext.getPermissions("courses") || [];

  if (
    !courseAccess.some((f) => f.access.includes(1)) ||
    authContext.onGoingValidations
  ) {
    navigate("/");
  }

  setShowAdd(courseAccess.some((f) => f.access.includes(2)));

}, [navigate, authContext]);

  const handleCourseAdded = () => {
    setReloadTable((prev) => !prev); 
  };

  const handleCloseModal = (saved) => {
    setShowModal(false);
    setSelectedCourseId(null);
  };

  const handleShowModal = () => {
    setSelectedCourseId(null);
    setShowModal(true);
  };

  const handleEditCourse = (courseId) => {
    setSelectedCourseId(courseId);
    setShowModal(true);
  };

  const initialIds = useMemo(() => ["courseCalendar", "totalCounts", "totalLearners"], []);

  const mergeOrder = (savedIds, defaultIds) => {
    const validSaved = savedIds.filter((id) => defaultIds.includes(id));
    const missing = defaultIds.filter((id) => !validSaved.includes(id));
    return [...validSaved, ...missing];
  };

  const [items, setItems] = useState(() => {
    const savedIds = loadFromLocalStorage();
    return mergeOrder(savedIds, initialIds);
  });

  const [activeId, setActiveId] = useState(null);

  const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 8 } });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 250, tolerance: 5 }
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      saveToLocalStorage(newItems);
    }
  };

  const renderChartById = (id) => {
    switch (id) {
      case "courseCalendar":
        return <CourseCalender />;
      case "totalCounts":
        return <TotalCountsCard />;
      case "totalLearners":
        return showAdd ? <TotalLearnersChart /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid custom-container mb-3">
      <div className="d-flex justify-content-end mb-3">
        {showAdd && (
          <button className="btn btn-primary py-2" onClick={handleShowModal}>
            <FaPlus className="pb-1 me-1" />Add New Course
          </button>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={horizontalListSortingStrategy}>
          <Row className="g-3 justify-content-center">
            {items.map((id) => (
              <SortableItem key={id} id={id} activeId={activeId}>
                {renderChartById(id)}
              </SortableItem>
            ))}
          </Row>
        </SortableContext>
      </DndContext>
      <RecentCoursesTable onEditCourse={handleEditCourse} reloadTrigger={reloadTable} />
      <AddCourseModal
        show={showModal}
        handleClose={handleCloseModal}
        courseId={selectedCourseId}
        onCourseAdded={handleCourseAdded}
      />
    </div>
  );
};

export default CoursesDashboard;